import React, { useState, useCallback, useEffect } from 'react';
import { useAddin } from '../App';
import MatterLookup from '../components/MatterLookup';
import ActionButton, { icons } from '../components/ActionButton';
import TaskModal from '../components/TaskModal';
import TimeEntryModal from '../components/TimeEntryModal';
import AddNoteModal from '../components/AddNoteModal';
import { DraftReplyModal } from '../components/DraftReplyModal';
import FileEmailModal from '../components/FileEmailModal';
import AddContactModal from '../components/AddContactModal';
import ContactModal from '../components/ContactModal';
import * as webhook from '../services/webhook';

/**
 * Actions tab — matter lookup + primary action buttons + conflict check.
 * This is the default tab shown in the reading pane.
 */
export default function ActionsTab() {
    const { linkedMatter, itemId, conversationId, userEmail, senderInfo } = useAddin();
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(null);
    const [summaryText, setSummaryText] = useState(null);

    // Modal visibility states
    const [showCreateTask, setShowCreateTask] = useState(false);
    const [showTimeEntry, setShowTimeEntry] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);
    const [showDraftReply, setShowDraftReply] = useState(false);
    const [showFileEmail, setShowFileEmail] = useState(false);
    const [showAddContact, setShowAddContact] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);

    const [matterDetails, setMatterDetails] = useState(null);

    const hasMatter = !!linkedMatter;

    // Fetch full matter details (contacts, custom fields) when matter is linked
    useEffect(() => {
        if (!linkedMatter?.id && !linkedMatter?.matter_id) {
            setMatterDetails(null);
            return;
        }
        let cancelled = false;
        webhook.getMatterDetails({ userEmail, matterId: linkedMatter.id || linkedMatter.matter_id })
            .then(res => { if (!cancelled) setMatterDetails(res.matter); })
            .catch(err => console.error('Failed to fetch matter details:', err));
        return () => { cancelled = true; };
    }, [linkedMatter?.id, linkedMatter?.matter_id, userEmail]);

    const showFeedback = (type, message, showTrigger = false) => {
        setFeedback({ type, message, showTrigger });
        if (type === 'success') {
            setTimeout(() => setFeedback(null), showTrigger ? 8000 : 4000);
        }
    };

    const handleTriggerFilerScan = useCallback(async () => {
        setLoading('trigger_scan');
        setFeedback(null);
        try {
            const token = localStorage.getItem('ioliberum_token');
            const res = await fetch('/api/webhook/trigger-full-scan', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            showFeedback('success', 'Filing scan triggered successfully!');
        } catch (err) {
            showFeedback('error', 'Failed to trigger filing: ' + err.message);
        } finally {
            setLoading(null);
        }
    }, []);

    // ── File Email ──
    const handleFileEmail = useCallback(() => {
        if (!hasMatter) return;
        setShowFileEmail(true);
    }, [hasMatter]);

    // ── Mark for Filing ──
    const handleMarkForFiling = useCallback(async () => {
        if (!hasMatter) return;
        setLoading('mark_for_filing');
        setFeedback(null);
        try {
            await webhook.markForFiling({
                outlookItemId: itemId,
                conversationId,
                userEmail,
                senderEmail: senderInfo?.email || '',
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.number || linkedMatter?.matter_number,
                matterDescription: linkedMatter?.description || linkedMatter?.matter_description,
                clientName: linkedMatter?.client_name || 'Unknown',
                senderInfo: senderInfo,
            });
            showFeedback('success', 'Email marked for filing and moved to "To Be Filed"', true);
        } catch (err) {
            showFeedback('error', err.message || 'Failed to mark email for filing');
        } finally {
            setLoading(null);
        }
    }, [itemId, conversationId, userEmail, linkedMatter, senderInfo, hasMatter]);

    // ── Summarize Thread ──
    const handleSummarize = useCallback(async () => {
        setLoading('summarize');
        setFeedback(null);
        setSummaryText(null);
        try {
            const result = await webhook.summarizeThread({
                outlookItemId: itemId,
                conversationId,
                userEmail,
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.number || linkedMatter?.matter_number,
            });
            if (result?.summary) {
                setSummaryText(result.summary);
            }
            showFeedback('success', 'Thread summarized and saved as note');
        } catch (err) {
            showFeedback('error', err.message || 'Failed to summarize thread');
        } finally {
            setLoading(null);
        }
    }, [itemId, conversationId, userEmail, linkedMatter]);

    return (
        <div className="addin-body">
            {/* Matter lookup — auto-resolves on mount */}
            <MatterLookup />

            {/* Feedback toast */}
            {feedback && (
                <div className={feedback.type === 'success' ? 'success-toast' : 'error-toast'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div>
                        {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                    </div>
                    {feedback.type === 'success' && feedback.showTrigger && (
                        <button
                            className="retry-btn"
                            disabled={loading === 'trigger_scan'}
                            onClick={handleTriggerFilerScan}
                            style={{
                                padding: '4px 8px',
                                fontSize: '10px',
                                border: '1px solid white',
                                borderRadius: '3px',
                                background: 'transparent',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            {loading === 'trigger_scan' ? 'Running...' : 'Run Filer Now'}
                        </button>
                    )}
                    {feedback.type === 'error' && (
                        <button className="retry-btn" onClick={() => setFeedback(null)}>Dismiss</button>
                    )}
                </div>
            )}

            <div className="section-label">Primary Actions</div>
            <div className="actions-grid">
                {/* File Now — full width */}
                <ActionButton
                    full
                    icon={icons.fileEmail}
                    iconClass="white"
                    label="File Now"
                    sub="Log + attachments → Clio"
                    accent="var(--orange)"
                    onClick={handleFileEmail}
                    disabled={!hasMatter || loading === 'file_email'}
                />

                {/* Mark for Filing — full width */}
                <ActionButton
                    full
                    icon={icons.fileEmail}
                    iconClass="teal"
                    label="Mark for Filing"
                    sub="Queue for background filing"
                    accent="var(--teal)"
                    onClick={handleMarkForFiling}
                    disabled={!hasMatter || loading === 'mark_for_filing'}
                />

                <ActionButton
                    icon={icons.createTask}
                    iconClass="teal"
                    label="Create Task"
                    sub="New Task"
                    accent="var(--teal)"
                    onClick={() => setShowCreateTask(true)}
                    disabled={!hasMatter}
                />

                <ActionButton
                    icon={icons.draftReply}
                    iconClass="orange"
                    label="Draft Reply"
                    sub="AI-assisted"
                    accent="var(--orange)"
                    onClick={() => setShowDraftReply(true)}
                    disabled={!hasMatter}
                />

                <ActionButton
                    icon={icons.timeEntry}
                    iconClass="navy"
                    label="Time Entry"
                    sub="From email"
                    accent="var(--navy)"
                    onClick={() => setShowTimeEntry(true)}
                    disabled={!hasMatter}
                />

                <ActionButton
                    icon={icons.addNote}
                    iconClass="teal"
                    label="Add Note"
                    sub="To matter"
                    accent="var(--teal)"
                    onClick={() => setShowAddNote(true)}
                    disabled={!hasMatter}
                />

                <ActionButton
                    icon={icons.summarize}
                    iconClass="navy"
                    label="Summarize"
                    sub="Thread → note"
                    accent="var(--grey02)"
                    onClick={handleSummarize}
                    disabled={!hasMatter || loading === 'summarize'}
                />

                <ActionButton
                    icon={icons.addContact}
                    iconClass="teal"
                    label="Add Contact"
                    sub="AI extract + Clio"
                    accent="var(--teal)"
                    onClick={() => setShowAddContact(true)}
                />
            </div>

            {/* Summary result display */}
            {summaryText && (
                <>
                    <div className="section-label">Thread Summary</div>
                    <div className="draft-area" style={{ minHeight: '60px' }}>
                        {summaryText}
                    </div>
                </>
            )}

            {/* Matter context — client, relationships & custom fields */}
            {hasMatter && (linkedMatter.client_name || matterDetails) && (
                <>
                    <div className="divider" />
                    <div className="section-label">Matter Parties</div>
                    <div className="info-block">
                        {(matterDetails?.client?.name || linkedMatter.client_name) && (
                            <div className="info-row">
                                <div className="info-key">Client</div>
                                <div className="info-val" style={{ fontWeight: 700 }}>
                                    {matterDetails?.client?.id ? (
                                        <span className="link" onClick={() => { setSelectedContactId(matterDetails.client.id); setShowContactModal(true); }}>
                                            {matterDetails.client.name}
                                        </span>
                                    ) : (
                                        matterDetails?.client?.name || linkedMatter.client_name
                                    )}
                                </div>
                            </div>
                        )}
                        {matterDetails?.contacts?.map((contact, i) => (
                            <div className="info-row" key={contact.id || i}>
                                <div className="info-key">{contact.relationship || 'Related'}</div>
                                <div className="info-val">
                                    {contact.id ? (
                                        <span className="link" onClick={() => { setSelectedContactId(contact.id); setShowContactModal(true); }}>
                                            {contact.name}
                                        </span>
                                    ) : (
                                        contact.name
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {matterDetails?.custom_fields?.length > 0 && (
                        <>
                            <div className="section-label">Custom Fields</div>
                            <div className="info-block">
                                {matterDetails.custom_fields.map((cf, i) => (
                                    <div className="info-row" key={cf.id || i}>
                                        <div className="info-key">{cf.name || 'Custom Field'}</div>
                                        <div className="info-val">{cf.value != null ? cf.value.toString() : '—'}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Modals */}
            {showCreateTask && (
                <TaskModal
                    isOpen={showCreateTask}
                    onClose={() => setShowCreateTask(false)}
                    onSuccess={() => showFeedback('success', 'Task created successfully')}
                />
            )}
            <TimeEntryModal
                isOpen={showTimeEntry}
                onClose={() => setShowTimeEntry(false)}
            />
            <AddNoteModal
                isOpen={showAddNote}
                onClose={() => setShowAddNote(false)}
            />
            <DraftReplyModal
                isOpen={showDraftReply}
                onClose={() => setShowDraftReply(false)}
            />
            {showFileEmail && (
                <FileEmailModal
                    isOpen={showFileEmail}
                    onClose={() => setShowFileEmail(false)}
                    onSuccess={() => showFeedback('success', 'Email thread queued for filing to Clio')}
                />
            )}
            {showAddContact && (
                <AddContactModal
                    isOpen={showAddContact}
                    onClose={() => setShowAddContact(false)}
                    onSuccess={() => showFeedback('success', 'Contact created in Clio')}
                />
            )}
            <ContactModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                contactId={selectedContactId}
            />
        </div>
    );
}
